import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, functions } from '../firebase';
import {
    collection, query, where, getDocs,
    doc, setDoc, getDoc, deleteDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import emailjs from '@emailjs/browser';
import './ForgotPassword.css';
import { FaExclamationTriangle, FaLock, FaShieldAlt } from 'react-icons/fa';
import { MdMail } from 'react-icons/md';

// ─── EmailJS Configuration ───────────────────────────────────────────────────
// Sign up at https://www.emailjs.com (free plan: 200 emails/month)
// Then replace these values:
const EMAILJS_SERVICE_ID  = 'YOUR_SERVICE_ID';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';  // e.g. 'template_xyz789'
const EMAILJS_PUBLIC_KEY  = 'YOUR_PUBLIC_KEY';   // e.g. 'abcDEFghiJKL'
// ─────────────────────────────────────────────────────────────────────────────

const OTP_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

const generateOTP = () =>
    Math.floor(100000 + Math.random() * 900000).toString();

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep]                     = useState(1); // 1: Email, 2: OTP, 3: New Password, 4: Success
    const [email, setEmail]                   = useState('');
    const [otp, setOtp]                       = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword]       = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew]               = useState(false);
    const [showConfirm, setShowConfirm]       = useState(false);
    const [errors, setErrors]                 = useState({});
    const [isLoading, setIsLoading]           = useState(false);
    const [resendTimer, setResendTimer]       = useState(0);
    const otpRefs                             = useRef([]);
    const timerRef                            = useRef(null);

    // ── Password strength ──
    const pwChecks = {
        length:  newPassword.length >= 6,
        upper:   /[A-Z]/.test(newPassword),
        number:  /[0-9]/.test(newPassword),
    };
    const pwStrength = Object.values(pwChecks).filter(Boolean).length;

    // ── Cleanup timer on unmount ──
    useEffect(() => () => clearInterval(timerRef.current), []);

    const startResendTimer = () => {
        setResendTimer(60);
        timerRef.current = setInterval(() => {
            setResendTimer(t => {
                if (t <= 1) { clearInterval(timerRef.current); return 0; }
                return t - 1;
            });
        }, 1000);
    };

    const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

    // ── Step 1: Verify admin email & send OTP ──
    const handleCheckEmail = async (e) => {
        e.preventDefault();
        if (!email.trim() || !validateEmail(email)) {
            setErrors({ email: 'Please enter a valid admin email' });
            return;
        }
        setIsLoading(true);
        setErrors({});
        try {
            // 1. Check Firestore users collection
            const q = query(collection(db, 'users'), where('email', '==', email.trim()));
            const snap = await getDocs(q);

            if (snap.empty) {
                setErrors({ email: 'No account found with this email' });
                return;
            }

            let isAdmin = false;
            snap.forEach(d => {
                const role = (d.data().role || '').toLowerCase();
                if (role === 'admin' || role === 'super admin') isAdmin = true;
            });

            if (!isAdmin) {
                setErrors({ email: 'Access denied — not an admin account' });
                return;
            }

            // 2. Generate & store OTP in Firestore
            const generatedOTP = generateOTP();
            const expiresAt    = Date.now() + OTP_EXPIRY_MS;
            const otpDocRef    = doc(db, 'passwordResetOtps', email.trim().toLowerCase());
            await setDoc(otpDocRef, { otp: generatedOTP, expiresAt, email: email.trim() });

            // 3. Send OTP via EmailJS
            try {
                await emailjs.send(
                    EMAILJS_SERVICE_ID,
                    EMAILJS_TEMPLATE_ID,
                    {
                        to_email:  email.trim(),
                        to_name:   'Admin',
                        otp_code:  generatedOTP,
                        expire_in: '10 minutes',
                    },
                    EMAILJS_PUBLIC_KEY
                );
            } catch (emailErr) {
                // If EmailJS not configured yet, show OTP in console (dev mode)
                console.warn('EmailJS not configured. OTP (dev only):', generatedOTP);
            }

            startResendTimer();
            setStep(2);
        } catch (err) {
            console.error(err);
            setErrors({ email: 'Something went wrong. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    // ── Step 2: Verify OTP ──
    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        const enteredOTP = otp.join('');
        if (enteredOTP.length < 6) {
            setErrors({ otp: 'Please enter all 6 digits' });
            return;
        }
        setIsLoading(true);
        setErrors({});
        try {
            const otpDocRef  = doc(db, 'passwordResetOtps', email.trim().toLowerCase());
            const otpSnap    = await getDoc(otpDocRef);

            if (!otpSnap.exists()) {
                setErrors({ otp: 'OTP expired or not found. Please request a new one.' });
                return;
            }

            const { otp: storedOTP, expiresAt } = otpSnap.data();

            if (Date.now() > expiresAt) {
                await deleteDoc(otpDocRef);
                setErrors({ otp: 'OTP has expired. Please request a new one.' });
                return;
            }

            if (enteredOTP !== storedOTP) {
                setErrors({ otp: 'Incorrect OTP. Please check and try again.' });
                return;
            }

            // OTP is valid — don't delete yet (needed until password is reset)
            setStep(3);
        } catch (err) {
            console.error(err);
            setErrors({ otp: 'Verification failed. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    // ── Step 3: Update Password via Cloud Function ──
    const handleChangePassword = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!newPassword) newErrors.newPassword = 'Password is required';
        else if (newPassword.length < 6) newErrors.newPassword = 'At least 6 characters required';
        if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

        setIsLoading(true);
        setErrors({});

        try {
            // Call Cloud Function — Admin SDK updates password, no re-auth needed
            const resetAdminPassword = httpsCallable(functions, 'resetAdminPassword');
            await resetAdminPassword({
                email: email.trim(),
                otp: otp.join(''),
                newPassword,
            });

            // Password updated — go straight to login
            navigate('/login', { replace: true });
        } catch (err) {
            console.error(err);
            const msg = err?.message || 'Something went wrong. Please try again.';
            setErrors({ general: msg });
        } finally {
            setIsLoading(false);
        }
    };

    // ── OTP input helpers ──
    const handleOTPChange = (index, value) => {
        if (!/^\d*$/.test(value)) return; // digits only
        const next = [...otp];
        next[index] = value.slice(-1); // one digit max
        setOtp(next);
        setErrors({});
        if (value && index < 5) otpRefs.current[index + 1]?.focus();
    };

    const handleOTPKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOTPPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pasted) return;
        const next = ['', '', '', '', '', ''];
        pasted.split('').forEach((ch, i) => { next[i] = ch; });
        setOtp(next);
        const focusIdx = Math.min(pasted.length, 5);
        otpRefs.current[focusIdx]?.focus();
    };

    const handleResend = () => {
        setOtp(['', '', '', '', '', '']);
        setErrors({});
        setStep(1);
    };

    const strengthLabel = ['', 'Weak', 'Fair', 'Strong'][pwStrength];
    const strengthColor = ['#ef4444', '#f59e0b', '#10b981'][pwStrength - 1] || '#ef4444';

    return (
        <div className="forgot-password-container fade-in">
            <div className="forgot-password-card">

                {/* ─── STEP 1: Email Entry ─────────────────────────────── */}
                {step === 1 && (
                    <>
                        <div className="forgot-header">
                            <div className="logo-container">
                                <img src="/bunchy-logo.svg" alt="Bunchy Logo" className="logo" />
                            </div>
                            <h1>Forgot Password</h1>
                            <p>Enter your admin email to receive a verification code</p>
                        </div>

                        <form onSubmit={handleCheckEmail} className="forgot-form">
                            <div className="form-group">
                                <label htmlFor="fp-email">Email Address</label>
                                <div className="input-wrapper">
                                    <span className="input-icon"><MdMail /></span>
                                    <input
                                        type="email"
                                        id="fp-email"
                                        value={email}
                                        onChange={e => { setEmail(e.target.value); setErrors({}); }}
                                        placeholder="admin@bunchy.com"
                                        className={`form-input ${errors.email ? 'error' : ''}`}
                                        autoComplete="email"
                                    />
                                </div>
                                {errors.email && (
                                    <span className="error-message">
                                        <FaExclamationTriangle /> {errors.email}
                                    </span>
                                )}
                            </div>

                            <button type="submit" className="btn-primary" disabled={isLoading}>
                                {isLoading ? <span className="loading-spinner" /> : 'Send OTP'}
                            </button>
                            <button type="button" onClick={() => navigate('/login')} className="btn-secondary">
                                ← Back to Login
                            </button>
                        </form>
                    </>
                )}

                {/* ─── STEP 2: OTP Verification ────────────────────────── */}
                {step === 2 && (
                    <>
                        <div className="forgot-header">
                            <div className="logo-container otp-icon-circle">
                                <FaShieldAlt size={36} color="var(--primary-blue)" />
                            </div>
                            <h1>Enter OTP</h1>
                            <p>We sent a 6-digit code to</p>
                            <p className="email-display">{email}</p>
                            <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                                Code expires in 10 minutes
                            </p>
                        </div>

                        <form onSubmit={handleVerifyOTP} className="forgot-form">
                            <div className="otp-container" onPaste={handleOTPPaste}>
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        id={`otp-${idx}`}
                                        ref={el => (otpRefs.current[idx] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleOTPChange(idx, e.target.value)}
                                        onKeyDown={e => handleOTPKeyDown(idx, e)}
                                        className={`otp-input ${errors.otp ? 'error' : ''}`}
                                        autoComplete="one-time-code"
                                    />
                                ))}
                            </div>

                            {errors.otp && (
                                <p className="error-message" style={{ textAlign: 'center' }}>
                                    <FaExclamationTriangle /> {errors.otp}
                                </p>
                            )}

                            <button type="submit" className="btn-primary" disabled={isLoading}>
                                {isLoading ? <span className="loading-spinner" /> : 'Verify OTP'}
                            </button>

                            <div style={{ textAlign: 'center' }}>
                                {resendTimer > 0 ? (
                                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        Resend OTP in <strong>{resendTimer}s</strong>
                                    </p>
                                ) : (
                                    <button type="button" className="resend-btn" onClick={handleResend}>
                                        Didn't receive the code? Resend
                                    </button>
                                )}
                            </div>

                            <button type="button" onClick={() => setStep(1)} className="btn-secondary">
                                ← Change Email
                            </button>
                        </form>
                    </>
                )}

                {/* ─── STEP 3: New Password ────────────────────────────── */}
                {step === 3 && (
                    <>
                        <div className="forgot-header">
                            <div className="logo-container otp-icon-circle">
                                <FaLock size={32} color="var(--primary-blue)" />
                            </div>
                            <h1>Set New Password</h1>
                            <p>Create a strong, secure password</p>
                        </div>

                        <form onSubmit={handleChangePassword} className="forgot-form">
                            {/* New Password */}
                            <div className="form-group">
                                <label htmlFor="new-pw">New Password</label>
                                <div className="input-wrapper">
                                    <span className="input-icon"><FaLock /></span>
                                    <input
                                        type={showNew ? 'text' : 'password'}
                                        id="new-pw"
                                        value={newPassword}
                                        onChange={e => { setNewPassword(e.target.value); setErrors({}); }}
                                        placeholder="Min. 6 characters"
                                        className={`form-input ${errors.newPassword ? 'error' : ''}`}
                                    />
                                    <button
                                        type="button"
                                        className="pw-toggle"
                                        onClick={() => setShowNew(p => !p)}
                                        aria-label="Toggle password visibility"
                                    >
                                        {showNew ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.newPassword && (
                                    <span className="error-message"><FaExclamationTriangle /> {errors.newPassword}</span>
                                )}
                                {/* Strength bar */}
                                {newPassword && (
                                    <div className="strength-bar-wrap">
                                        <div className="strength-bar">
                                            {[1,2,3].map(i => (
                                                <div
                                                    key={i}
                                                    className="strength-segment"
                                                    style={{ background: i <= pwStrength ? strengthColor : 'var(--border-color)' }}
                                                />
                                            ))}
                                        </div>
                                        <span className="strength-label" style={{ color: strengthColor }}>
                                            {strengthLabel}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div className="form-group">
                                <label htmlFor="confirm-pw">Confirm Password</label>
                                <div className="input-wrapper">
                                    <span className="input-icon"><FaLock /></span>
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        id="confirm-pw"
                                        value={confirmPassword}
                                        onChange={e => { setConfirmPassword(e.target.value); setErrors({}); }}
                                        placeholder="Re-enter your new password"
                                        className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                                    />
                                    <button
                                        type="button"
                                        className="pw-toggle"
                                        onClick={() => setShowConfirm(p => !p)}
                                        aria-label="Toggle confirm visibility"
                                    >
                                        {showConfirm ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <span className="error-message"><FaExclamationTriangle /> {errors.confirmPassword}</span>
                                )}
                            </div>

                            {errors.general && errors.general !== 'REAUTH_NEEDED' && (
                                <div className="message error"><FaExclamationTriangle /> {errors.general}</div>
                            )}

                            <div className="password-requirements">
                                <p className="requirements-title">Password must have:</p>
                                <ul>
                                    <li className={pwChecks.length ? 'valid' : ''}>
                                        {pwChecks.length ? '✅' : '⬜'} At least 6 characters
                                    </li>
                                    <li className={pwChecks.upper ? 'valid' : ''}>
                                        {pwChecks.upper ? '✅' : '⬜'} One uppercase letter
                                    </li>
                                    <li className={pwChecks.number ? 'valid' : ''}>
                                        {pwChecks.number ? '✅' : '⬜'} One number
                                    </li>
                                </ul>
                            </div>

                            <button type="submit" className="btn-primary" disabled={isLoading}>
                                {isLoading ? <span className="loading-spinner" /> : 'Update Password'}
                            </button>
                        </form>
                    </>
                )}


            </div>
        </div>
    );
};

export default ForgotPassword;
